import { useSyncExternalStore } from "react";
import {
  getSemanticStageValue,
  type FactorId,
  type SerializableControlValues
} from "../chapters/chapterLabModels";
import type { SceneId, SceneProgress } from "../types";

export type NatureStageSnapshot =
  | { mode: "ambient"; sceneId: SceneId | null }
  | { mode: "phenology"; sceneId: "C2-00"; stage: number; focusedKind?: string }
  | {
      mode: "factor";
      sceneId: "C3-01" | "C3-02" | "C3-03" | "C3-04";
      factorId: FactorId;
      controls: SerializableControlValues;
      normalizedValue: number;
    };

const factorByScene = {
  "C3-01": "latitude",
  "C3-02": "longitude",
  "C3-03": "elevation",
  "C3-04": "era"
} as const satisfies Partial<Record<SceneId, FactorId>>;

const observationKinds = ["peach", "bird", "insect", "weather"] as const;

function clampPhenologyStage(value: unknown): number {
  const stage = typeof value === "number" && Number.isFinite(value) ? value : 0;
  return Math.max(0, Math.min(3, stage));
}

/**
 * Derives the renderer state from the same routed scene progress consumed by the
 * task UI. This keeps scene navigation and the 3D request in one React render.
 */
export function deriveNatureStageSnapshot(
  sceneId: SceneId,
  sceneProgress: Pick<SceneProgress, "sceneId" | "visualState">
): NatureStageSnapshot {
  const controls = sceneProgress.sceneId === sceneId
    ? { ...sceneProgress.visualState.controlValues }
    : {};

  if (sceneId === "C2-00") {
    const focusedKind = String(controls.focusedKind ?? "peach");
    return {
      mode: "phenology",
      sceneId,
      stage: clampPhenologyStage(controls.stage),
      focusedKind: observationKinds.includes(focusedKind as (typeof observationKinds)[number])
        ? focusedKind
        : "peach"
    };
  }

  const factorSceneId = sceneId as keyof typeof factorByScene;
  const factorId = factorByScene[factorSceneId];
  if (factorId) {
    const semanticControls = controls as SerializableControlValues;
    return {
      mode: "factor",
      sceneId: factorSceneId,
      factorId,
      controls: semanticControls,
      normalizedValue: getSemanticStageValue(factorId, semanticControls)
    };
  }

  return { mode: "ambient", sceneId };
}

let snapshot: NatureStageSnapshot = { mode: "ambient", sceneId: null };
const listeners = new Set<() => void>();

export function publishNatureStageState(next: NatureStageSnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getNatureStageState(): NatureStageSnapshot {
  return snapshot;
}

export function subscribeNatureStageState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNatureStageState(): NatureStageSnapshot {
  return useSyncExternalStore(subscribeNatureStageState, getNatureStageState, getNatureStageState);
}
